import {
  CalendarDaysIcon,
  ChartBarSquareIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  PresentationChartLineIcon,
  RectangleGroupIcon,
  Squares2X2Icon,
  TicketIcon,
} from "@heroicons/react/24/outline";
import { lazy } from "react";

import type { FeatureMetadata } from "../feature.types";
import { buildCrudPermissions } from "../shared/featureFactories";

export const ExamsFeature: FeatureMetadata = {
  id: "exams",
  moduleName: "examinations",
  navigation: {
    label: "Exams",
    path: "/exams",
    icon: ClipboardDocumentListIcon,
    order: 1,
  },
  permissions: buildCrudPermissions("Examinations", "Exams"),
  route: {
    path: "/exams/*",
    element: lazy(() => import("./pages/ExamsRoot")),
  },
  readiness: "live",
};

export const DashboardFeature: FeatureMetadata = {
  id: "examinations-dashboard",
  moduleName: "examinations",
  navigation: {
    label: "Dashboard",
    path: "/exams/overview",
    icon: Squares2X2Icon,
    order: 0,
  },
  permissions: buildCrudPermissions("Examinations", "Exams"),
  route: {
    path: "/exams/overview",
    element: lazy(() => import("./pages/aggregate/OverviewAggregatePage")),
  },
  readiness: "live",
};

export const TimetableFeature: FeatureMetadata = {
  id: "examinations-timetable",
  moduleName: "examinations",
  navigation: {
    label: "Timetable",
    path: "/exams/timetable",
    icon: CalendarDaysIcon,
    order: 2,
  },
  guardianNavigation: { order: 6 },
  permissions: buildCrudPermissions("Examinations", "Timetable", {
    edit: "Examinations.Timetable.edit",
  }),
  route: {
    path: "/exams/timetable",
    element: lazy(() => import("./pages/aggregate/TimetableAggregatePage")),
  },
  readiness: "live",
};

export const HallTicketsFeature: FeatureMetadata = {
  id: "examinations-hall-tickets",
  moduleName: "examinations",
  navigation: {
    label: "Hall Tickets",
    path: "/exams/hall-tickets",
    icon: TicketIcon,
    order: 3,
  },
  guardianNavigation: { order: 7 },
  permissions: buildCrudPermissions("Examinations", "HallTickets", {
    create: "Examinations.HallTickets.generate",
    edit: "Examinations.HallTickets.generate",
  }),
  route: {
    path: "/exams/hall-tickets",
    element: lazy(() => import("./pages/aggregate/HallTicketsAggregatePage")),
  },
  readiness: "live",
};

export const MarksEntryFeature: FeatureMetadata = {
  id: "marks-entry",
  moduleName: "examinations",
  navigation: {
    label: "Marks Entry",
    path: "/exams/marks",
    icon: PencilSquareIcon,
    order: 4,
  },
  guardianNavigation: { label: "Marks", order: 8 },
  permissions: buildCrudPermissions("Examinations", "MarksEntry", {
    create: "Examinations.MarksEntry.enter",
    edit: "Examinations.MarksEntry.edit",
    delete: "Examinations.MarksEntry.edit",
  }),
  route: {
    path: "/exams/marks",
    element: lazy(() => import("./pages/aggregate/MarksAggregatePage")),
  },
  readiness: "live",
};

export const GradebookFeature: FeatureMetadata = {
  id: "gradebook",
  moduleName: "examinations",
  navigation: {
    label: "Gradebook",
    path: "/exams/gradebook",
    icon: DocumentTextIcon,
    order: 5,
  },
  guardianNavigation: { order: 9 },
  permissions: buildCrudPermissions("Examinations", "Gradebook", {
    create: "Examinations.Gradebook.configure",
    edit: "Examinations.Gradebook.configure",
    delete: "Examinations.Gradebook.configure",
  }),
  route: {
    path: "/exams/gradebook",
    element: lazy(() => import("./pages/GradebookPage")),
  },
  readiness: "live",
};

export const ResultProcessingFeature: FeatureMetadata = {
  id: "result-processing",
  moduleName: "examinations",
  navigation: {
    label: "Result Processing",
    path: "/exams/result-processing",
    icon: RectangleGroupIcon,
    order: 6,
  },
  permissions: buildCrudPermissions("Examinations", "ResultProcessing", {
    view: "Examinations.ResultProcessing.process",
    create: "Examinations.ResultProcessing.process",
    edit: "Examinations.ResultProcessing.autoValidate",
    delete: "Examinations.ResultProcessing.autoValidate",
  }),
  route: {
    path: "/exams/result-processing",
    element: lazy(() => import("./pages/aggregate/ResultProcessingAggregatePage")),
  },
  readiness: "live",
};

export const ReportCardsFeature: FeatureMetadata = {
  id: "report-cards",
  moduleName: "examinations",
  navigation: {
    label: "Report Cards",
    path: "/exams/report-cards",
    icon: ChartBarSquareIcon,
    order: 7,
  },
  guardianNavigation: { order: 10 },
  permissions: buildCrudPermissions("Examinations", "ReportCards", {
    create: "Examinations.ReportCards.generate",
    edit: "Examinations.ReportCards.designTemplate",
    delete: "Examinations.ReportCards.designTemplate",
  }),
  route: {
    path: "/exams/report-cards",
    element: lazy(() => import("./pages/ReportCardsPage")),
  },
  readiness: "live",
};

export const PerformanceAnalyticsFeature: FeatureMetadata = {
  id: "performance-analytics",
  moduleName: "examinations",
  navigation: {
    label: "Analytics",
    path: "/exams/analytics",
    icon: PresentationChartLineIcon,
    order: 8,
  },
  guardianNavigation: { order: 11 },
  permissions: buildCrudPermissions("Examinations", "PerformanceAnalytics", {
    create: "Examinations.PerformanceAnalytics.predictive",
    edit: "Examinations.PerformanceAnalytics.predictive",
    delete: "Examinations.PerformanceAnalytics.predictive",
  }),
  route: {
    path: "/exams/analytics",
    element: lazy(() => import("./pages/aggregate/AnalyticsAggregatePage")),
  },
  readiness: "live",
};

export const ExamSettingsFeature: FeatureMetadata = {
  id: "examinations-settings",
  moduleName: "examinations",
  navigation: {
    label: "Settings",
    path: "/exams/settings",
    icon: Cog6ToothIcon,
    order: 9,
  },
  permissions: buildCrudPermissions("Examinations", "Settings", {
    view: "Examinations.Settings.manage",
    create: "Examinations.Settings.manage",
    edit: "Examinations.Settings.manage",
    delete: "Examinations.Settings.manage",
  }),
  route: {
    path: "/exams/settings",
    element: lazy(() => import("./pages/aggregate/SettingsAggregatePage")),
  },
  readiness: "live",
};

export const EXAMINATIONS_FEATURES: readonly FeatureMetadata[] = [
  DashboardFeature,
  ExamsFeature,
  TimetableFeature,
  HallTicketsFeature,
  MarksEntryFeature,
  GradebookFeature,
  ResultProcessingFeature,
  ReportCardsFeature,
  PerformanceAnalyticsFeature,
  ExamSettingsFeature,
];
